import { useEffect, useState, type ChangeEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { itemLabel, itemTitle, type AdminItem, type AdminSale, type ItemStatus } from '@sellit/shared';
import { api, ApiError } from '../lib/api';
import { setAppTitle, STATUS_LABEL } from '../lib/format';
import { Icon } from '../lib/Icon';
import { Wordmark } from '../lib/Brand';
import { publicUrl, shareLink } from '../lib/Share';
import { useMe } from './auth';

type Filter = 'ALL' | ItemStatus;
const FILTERS: Filter[] = ['ALL', 'AVAILABLE', 'PENDING', 'SOLD'];
const SALE_PILL = { ACTIVE: 'Live', DRAFT: 'Draft · not public yet', CLOSED: 'Closed', ARCHIVED: 'Archived · hidden' } as const;
const QUICK_STATUSES = ['AVAILABLE', 'PENDING', 'SOLD'] as const satisfies readonly ItemStatus[];

export function SaleDashboard() {
  const { saleId } = useParams();
  const navigate = useNavigate();
  const me = useMe();
  const [sale, setSale] = useState<AdminSale | null>(null);
  const [items, setItems] = useState<AdminItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [filter, setFilter] = useState<Filter>('ALL');
  const [openId, setOpenId] = useState<string | null>(null);
  const isOwner = me.user.role === 'PLATFORM_ADMIN' || me.sales.some((s) => s.id === saleId && s.role === 'OWNER');

  useEffect(() => {
    if (!saleId) return;
    api.sale(saleId).then(
      (s) => {
        setSale(s);
        setAppTitle(`${s.title}`);
      },
      (err: ApiError) => setError(err.message),
    );
  }, [saleId]);

  // Archived items are only fetched when that filter is picked.
  useEffect(() => {
    if (!saleId) return;
    setItems(null);
    api.items(saleId, filter === 'ARCHIVED' ? 'ARCHIVED' : undefined).then(
      (i) => setItems(i.items),
      (err: ApiError) => setError(err.message),
    );
  }, [saleId, filter === 'ARCHIVED']);

  async function setStatus(item: AdminItem, status: ItemStatus) {
    setOpenId(null);
    const before = items;
    setItems((list) => list?.map((i) => (i.id === item.id ? { ...i, status } : i)) ?? null);
    try {
      const saved = await api.updateItem(item.id, { status });
      setItems((list) =>
        list?.flatMap((i) => (i.id !== saved.id ? [i] : (status === 'ARCHIVED') !== (filter === 'ARCHIVED') ? [] : [saved])) ?? null,
      );
    } catch (err) {
      setItems(before);
      setError(err instanceof ApiError ? err.message : 'Could not update. Try again.');
    }
  }

  // "Take photo" opens the camera in one tap; the photos travel to the Add Item screen.
  function onPhotos(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length) navigate(`/admin/sales/${saleId}/add`, { state: { files } });
  }

  // The phone's share sheet where there is one; otherwise the link is copied.
  async function share(title: string, url: string) {
    if ((await shareLink(title, url)) === 'copied') {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  async function logout() {
    await api.logout().catch(() => undefined);
    navigate('/admin/login', { replace: true });
  }

  if (error && !sale) return <main className="page"><p className="error">{error}</p></main>;
  if (!sale) return <main className="page center muted">Loading…</main>;
  const shown = items?.filter((i) => filter === 'ALL' || filter === 'ARCHIVED' || i.status === filter) ?? null;

  const hidden = sale.status === 'DRAFT' || sale.status === 'ARCHIVED';
  const liveCount = items?.filter((i) => i.status !== 'SOLD' && i.status !== 'ARCHIVED').length;

  return (
    <main className="page">
      <header className="app-bar">
        <div className="app-bar-title">
          {me.user.role === 'PLATFORM_ADMIN' ? (
            <Link to={`/platform-admin/sales/${sale.id}`} className="muted small">← Platform admin</Link>
          ) : (
            me.sales.length > 1 && <Link to="/admin" className="muted small">← All sales</Link>
          )}
          <Wordmark />
        </div>
        <button className="icon-btn" onClick={logout} aria-label="Log out" title="Log out"><Icon name="logout" /></button>
      </header>

      <section className="dash-head">
        <h1>{sale.title}</h1>
        <span className={`chip sale-${sale.status.toLowerCase()}`}>{SALE_PILL[sale.status]}</span>
      </section>

      <div className="dash-add">
        <label className="btn primary big">
          <Icon name="camera" size={22} /> Add Item
          <input type="file" accept="image/*" capture="environment" hidden onChange={onPhotos} />
        </label>
        <label className="link small">
          or choose photos from your library
          <input type="file" accept="image/*" multiple hidden onChange={onPhotos} />
        </label>
      </div>

      <nav className="dash-tiles" aria-label="Sale tools">
        <a className="dash-tile" href="#my-items">
          <Icon name="tag" />
          <span>My Items</span>
          <small>{liveCount ?? '…'} for sale</small>
        </a>
        <Link className="dash-tile" to={`/admin/sales/${sale.id}/inquiries`}>
          <Icon name="inbox" />
          <span>My Buyers</span>
          <small>{sale.newInquiryCount > 0 ? `${sale.newInquiryCount} new` : 'No new buyers'}</small>
          {sale.newInquiryCount > 0 && <span className="badge" aria-hidden="true">{sale.newInquiryCount}</span>}
        </Link>
        <button className="dash-tile" onClick={() => share(sale.title, publicUrl(sale.slug))}>
          <Icon name={copied ? 'check' : 'share'} />
          <span>{copied ? 'Link copied' : 'Share My Sale'}</span>
          <small>Send one link</small>
        </button>
      </nav>

      <section aria-labelledby="my-items">
        <h2 id="my-items" className="dash-section-title">{filter === 'ARCHIVED' ? 'Archived items' : 'My Items'}</h2>
        {filter !== 'ARCHIVED' && (
          <div className="filters" role="tablist" aria-label="Show">
            {FILTERS.map((f) => (
              <button key={f} role="tab" aria-selected={filter === f} className={filter === f ? 'on' : ''} onClick={() => setFilter(f)}>
                {f === 'ALL' ? 'All' : STATUS_LABEL[f]}
              </button>
            ))}
          </div>
        )}

        {error && <p className="error" role="alert">{error}</p>}
        {!shown ? (
          <p className="center muted">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="empty muted">
            {filter === 'ALL' ? <>No items yet. Tap <strong>Add Item</strong> to photograph your first one.</> : 'No items here.'}
          </p>
        ) : (
          <ul className="item-list">
            {shown.map((item) => (
              <li key={item.id} className={`item-entry${openId === item.id ? ' open' : ''}`}>
                <button
                  className="item-row"
                  aria-expanded={openId === item.id}
                  onClick={() => setOpenId(openId === item.id ? null : item.id)}
                >
                  {item.thumbUrl ? <img src={item.thumbUrl} alt="" loading="lazy" /> : <span className="noimg" />}
                  <span className="item-row-text">
                    <strong>{itemLabel(item)}</strong>
                    <span>{item.priceLabel}</span>
                  </span>
                  <span className={`chip ${item.status.toLowerCase()}`}>{STATUS_LABEL[item.status]}</span>
                </button>
                {openId === item.id && (
                  <div className="row-actions">
                    {item.status === 'ARCHIVED' ? (
                      <button className="btn" onClick={() => setStatus(item, 'AVAILABLE')}>Unarchive</button>
                    ) : (
                      QUICK_STATUSES.filter((s) => s !== item.status).map((s) => (
                        <button key={s} className={`btn status-${s.toLowerCase()}`} onClick={() => setStatus(item, s)}>
                          Mark {STATUS_LABEL[s]}
                        </button>
                      ))
                    )}
                    {item.status !== 'ARCHIVED' && (
                      <button className="btn" onClick={() => share(`${itemTitle(item)} · ${item.priceLabel}`, publicUrl(sale.slug, item.number))}>
                        <Icon name="share" size={18} /> Share
                      </button>
                    )}
                    <Link className="btn" to={`/admin/sales/${sale.id}/items/${item.id}`}>Edit</Link>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <nav className="dash-more" aria-label="More">
        <a className="link" href={`/${sale.slug}`} target="_blank" rel="noreferrer">
          <Icon name="eye" size={18} /> {hidden ? 'Preview My Sale' : 'See My Sale'}
        </a>
        {isOwner && (
          <Link className="link" to={`/admin/sales/${sale.id}/settings`}>
            <Icon name="gear" size={18} /> Sale Details
          </Link>
        )}
        <button className="link" onClick={() => setFilter(filter === 'ARCHIVED' ? 'ALL' : 'ARCHIVED')}>
          <Icon name="inbox" size={18} /> {filter === 'ARCHIVED' ? 'Back to my items' : 'Archived items'}
        </button>
      </nav>
    </main>
  );
}
